import React, { FC, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { Header } from 'components/Header';
import { BottomNav, BOTTOM_NAV_RESERVED } from 'components/UI/BottomNav';
import { SideNav } from 'components/UI/SideNav';
import { showsPrimaryNav } from 'components/UI/primaryNav';
import { slideInSx } from 'utils/pageTransition';
import type { SlideDirection } from 'utils/pageTransition';

import { Box, Stack } from '@mui/material';
import { APP_MAX_WIDTH, SIDE_NAV_WIDTH } from 'constants/layout';
import { GoogleOAuthProvider } from '@react-oauth/google';

export const LayoutDefault: FC = () => {
  const googleClientId = import.meta.env.GOOGLE_CLIENT_ID;
  const location = useLocation();
  const locationPath = location.pathname;
  /* Set by the bottom nav from the tab index delta; absent for every other
     navigation, which therefore does not animate. */
  const tabSlide = (location.state as { tabSlide?: SlideDirection } | null)?.tabSlide;

  /*
   * The slide is armed for ONE path and disarmed the moment it finishes.
   *
   * It must not persist: the animation carries `fill: both`, so leaving it
   * applied leaves a transform on the element forever — an identity transform,
   * but a transform, and a transformed ancestor becomes the containing block for
   * every `position: fixed` descendant. That is why the certificate header is
   * `sticky` rather than `fixed`.
   *
   * Recording the FINISHED path rather than the armed one, which is what makes
   * this work on the first render. Arming from an effect meant the element
   * mounted with no animation and only got one after the effect had run, a frame
   * later — the slide either played late or, as measured, not at all. Here
   * `finishedPath` still holds the PREVIOUS path at the moment the new one
   * renders, so `active` is true immediately, with no effect and no race.
   */
  const [finishedPath, setFinishedPath] = useState<string | null>(null);
  const slideActive = Boolean(tabSlide) && finishedPath !== locationPath;

  /*
   * Flips per path so the keyframe name alternates and the animation restarts
   * without remounting the subtree.
   *
   * Set during render rather than in an effect for the same reason `finishedPath`
   * is: an effect runs a frame late, and the element needs its animation on the
   * render that first shows the new path.
   */
  const lastPath = useRef(locationPath);
  const cycle = useRef<'A' | 'B'>('A');
  if (lastPath.current !== locationPath) {
    lastPath.current = locationPath;
    cycle.current = cycle.current === 'A' ? 'B' : 'A';
  }

  /*
   * There was a DESKTOP fade here (`fadeArmed` / `fadeActive` / `fadeInSx`) and it is
   * gone. `utils/pageTransition` carries the full reasoning; the short version is that
   * `apps/platform` — the surface this app is supposed to match — has NO route-level
   * animation at all. Its `Layout` renders `<Outlet />` in a plain Box. What reads as a
   * platform page transition is the loading handoff: a footprint-parity skeleton held
   * by `useDeferredLoading` (180ms grace, 320ms floor), then `<Fade in timeout={120}>`
   * over the arriving page.
   *
   * So the transition moved OUT of this layout and INTO the pages, where the data is.
   * That is not a relocation of the same effect — a layout-level fade fires on the route
   * change, before anything has loaded, and necessarily animates whatever is underneath;
   * the page-level one fires when the content actually arrives, over a footprint its
   * skeleton already reserved. Only the second can be honest about what it is showing.
   *
   * `hasInteracted` went with it. Its only job was gating the fade against a CSS
   * animation stalling at `currentTime: 0` on a navigation nothing was driving frames
   * for (the `/` -> `/certificates` boot redirect was the measured case). MUI's `Fade`
   * is a JS-driven transition on a mounting element, so it has no equivalent failure
   * mode and needs no equivalent guard.
   *
   * The SLIDE is untouched below — mobile keeps its push, armed by router state exactly
   * as before.
   */


  //remove query params from url if not certificate path
  useEffect(() => {
    const isCertificatePath = locationPath.includes('/certificates/') && locationPath.split('/')[2];

    if (!isCertificatePath && window.location.search) {
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [locationPath]);

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      {/*
        THE PAGE ROW: [desktop rail][content column].

        The row is capped at `APP_MAX_WIDTH + SIDE_NAV_WIDTH` rather than at
        `APP_MAX_WIDTH`, and that is the whole width decision — it is what lets the
        rail be free above 1480px instead of always costing content.

          viewport >= 1480  row caps at 1480, rail 200, content a full 1280. The
                            rail sits in the page's own margin and the content
                            column, the card grid and the toolbar are all unchanged.
          viewport <  1480  row is the viewport, rail 200, content 100vw - 200. The
                            content column narrows.

        At a 1280 viewport that second case is content 1080 and cards 317 (from 1184
        and 384). There is no arrangement that avoids it: 1280px of content plus a
        rail does not fit in 1280px, so the alternatives are overlapping the cards or
        having no rail. See `constants/layout` `SIDE_NAV_WIDTH` for the one-line
        `HeaderBar` change that keeps the search field aligned to a card below 1480.

        The rail is inside the row rather than `position: fixed` on the window so it
        stays attached to the centred page rather than pinning to the window edge —
        and because a `fixed` element cannot survive the tab slide's transformed
        ancestor (see `overflowX` below).
      */}
      <Stack
        direction='row'
        sx={{
          width: '100%',
          maxWidth: `${APP_MAX_WIDTH + SIDE_NAV_WIDTH}px`,
          margin: '0 auto',
        }}
      >
        <SideNav />
        <Stack
          component='main'
          sx={{
          /*
            A CEILING, not a cage — see constants/layout for why that distinction
            cost a round trip. The certificates list already lays itself out in a
            multi-column grid as the viewport grows; this only stops it running
            edge-to-edge on a monitor wider than the layout was designed for.

            `flex: '1 1 auto'` + `minWidth: 0` rather than `width: '100%'`: as a
            flex child, `width: 100%` would resolve against the ROW and refuse to
            shrink for the rail, overflowing it off the right edge. `minWidth: 0`
            is the standard escape from flexbox's `min-width: auto` floor, which
            otherwise lets wide content (a long title, the card grid) push this
            column past its share and shove the rail off-screen.
          */
          flex: '1 1 auto',
          minWidth: 0,
          maxWidth: `${APP_MAX_WIDTH}px`,
          margin: '0 auto',
          /*
           * Clip the slide's off-screen half, or the bottom bar flashes — and
           * ONLY in one direction, which is what finally identified this.
           *
           * The tab slide starts the incoming page at `translateX(±100%)`. At
           * +100% that is a full viewport width sticking out past the RIGHT
           * edge, which expands the document's scrollable width and briefly
           * makes the page horizontally scrollable. At −100% it hangs off the
           * LEFT, and browsers do not create scrollable overflow on the start
           * side in LTR — it is simply not reachable. So the two directions are
           * not symmetric, and the layout viewport changes under a
           * `bottom`-anchored `position: fixed` bar in one of them and not the
           * other.
           *
           * That asymmetry is the whole diagnosis. It was reported exactly this
           * way — flashes going Certificates -> Activity and Activity ->
           * Profile, never on the way back — and no theory about unmounting,
           * emotion class churn or backdrop-filter layer promotion predicts a
           * direction-dependent symptom. Those were all checked and cleared
           * first: a MutationObserver across a switch records no removal, no
           * re-add and no class change.
           *
           * `clip`, NOT `hidden`. `hidden` turns this into a scroll container,
           * which creates a containing block for absolutely-positioned
           * descendants and breaks `position: sticky` for them — and the
           * certificate page's NavigationHeader is sticky precisely because
           * `fixed` already fails there. `clip` clips without becoming a scroll
           * container, and is the one overflow value that still permits
           * `visible` on the other axis, so vertical page scrolling is
           * untouched.
           */
          overflowX: 'clip',
          //need for safari browser
          '@supports (-webkit-touch-callout: none)': {
            minHeight: '-webkit-fill-available',
          },
          /*
           * Reserve the bar's space so a page's last row is not sitting under it.
           * `position: fixed` takes the bar out of flow, so without this the
           * footer, the Log out button and the final list card are all reachable
           * only by scrolling past content the bar is covering.
           *
           * Applied at the layout rather than per page so a new page cannot
           * forget it. Mobile only, matching the bar itself.
           *
           * Reserved only where the bar actually SHOWS. It hides on pushed
           * pages (a certificate, its share screen), and reserving there left
           * ~88px of empty page under content with nothing in it — visible as a
           * dead gap below the certificate page's last row, and the space the
           * sticky action bar now needs to sit against the bottom edge.
           */
          paddingBottom: {
            xs: showsPrimaryNav(locationPath)
              ? `calc(${BOTTOM_NAV_RESERVED}px + env(safe-area-inset-bottom, 0px))`
              : 0,
            md: 0,
          },
          }}
        >
          {/* <Header /> */}
          {/*
            ONE place, not three: every tab destination renders through this outlet,
            so the incoming animation lives here rather than being re-implemented in
            Certificates, Activity and Profile.

            The wrapper is LAYOUT-NEUTRAL by construction — flex column, `flex: 1`,
            full width — because the page used to be a direct flex child of this
            `main` Stack and still has to behave like one. The first version was a
            bare `<Box>`, which silently inserted a non-flex node into that chain:
            the Profile page (long enough to matter) stopped stretching, the scroll
            container grew, and the fixed bottom bar ended up positioned against a
            taller box than the viewport — the bar and the page both "messed up".
          */}
          <Box
            /*
              `e.target === e.currentTarget` is load-bearing: animationend BUBBLES,
              and this wrapper contains plenty of other animations — the skeleton
              shimmer, the status badge's entrance. Without the guard, the first
              child animation to finish disarmed the slide, which is exactly why it
              stopped playing entirely after the previous fix.
            */
            onAnimationEnd={e => {
              if (e.target === e.currentTarget) setFinishedPath(locationPath);
            }}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: '1 1 auto',
              width: '100%',
              minWidth: 0,
              /* The only page-level animation left, and it is mobile-only: armed by
                 router state (`tabSlide`), and `shouldSlide()` is <900px. Desktop's
                 transition now lives in the pages themselves (skeleton -> Fade), so
                 there is nothing here for it to conflict with. */
              ...slideInSx(slideActive, tabSlide ?? 'right', cycle.current),
            }}
          >
            <Outlet />
          </Box>
        </Stack>
      </Stack>
      <BottomNav />
    </GoogleOAuthProvider>
  );
};
